import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import bind from 'bind-decorator';
import i18next from 'i18next';
import { computed, reaction, makeObservable, observable, action } from 'mobx';

import { ColorDialogColors, DialogType } from './Constants';
import { ColorDialogConfiguration } from './ColorDialog';
import { CookieUserSetting } from './cookieDialog/CookieUserSettingManager';
import { ErrorDialogConfiguration } from './errorDialog/ErrorDialog';
import { InputDialogConfig, ModalInputDialogConfig } from './InputDialogBase';
import { InputDialogOptions, noClose } from './InputDialog';
import { LoadingDialogConfiguration, QrDialogConfiguration } from './utils/DialogUtils';

import type { App } from '@egr/xbox/base-app/App';
import { AppStateManager } from '@egr/xbox/base-app/managers/Manager';
import { DialogErrorMessage } from '@egr/xbox/egr-gui-dialogs/base/DialogErrorMessage';
import { FEEDBACK_EMAIL } from '@egr/xbox/utils/Constants';
import { PlayVideoProgress, PlayVideoProgressEvent } from '@egr/xbox/app-api/Communication';
import { addBreadcrumb } from '@egr/xbox/utils/Error';
import { android, getIOSVersion, ios, inPlanner } from '@egr/xbox/utils/UserAgent';
import { appInfo } from '@egr/xbox/app-api/AppInfo';
import { captureDataException } from '@egr/xbox/utils/errors/DataErrors';
import { copyToClipBoard } from '@egr/xbox/utils/String';
import { debugTap } from '@egr/xbox/egr-gui-elements/Helper/Debug';
import { doNothing } from '@egr/xbox/utils/Helper';
import { downloadUrl } from '@egr/xbox/utils/Download';
import { getCachedValue } from '@egr/xbox/utils/Cache';
import { getDataErrorFeedbackButton } from '@egr/xbox/app-api/Utils';
import { multilineTextToArray } from '@egr/xbox/egr-gui-utils/StringHelper';
import { notNull } from '@egr/xbox/utils/Types';
import { shareText } from '@egr/xbox/app-api/Share';
import { startVideoPlayback } from '@egr/xbox/app-api/AppAction';
import { textLine } from '@egr/xbox/egr-gui-elements/Helper/MUIUtils';
import { trackEvent } from '@egr/xbox/telemetry/MatomoUtils';

import { AppCallbacks } from '@egr/wcf/modules/core';
import { isNotNullOrEmpty, isNullOrEmpty } from '@egr/wcf/modules/utils/string';

type YoutubeVideoStatus = 'buffering' | 'running' | 'ended' | 'error';

type IframeBarStyle = 'default' | 'justClose' | 'hidden';

interface DialogManagerState {
    cookieDialog: boolean;
    resolveCookieDialog: null | ((value: CookieUserSetting) => void);

    iframeUri: string | undefined;
    iframeTitle: string;
    iframeBarStyle: IframeBarStyle;

    showDialogFullscreen: boolean;

    youtubeLinkId: string | undefined;
    youtubeTitle: string | undefined;
    youtubeDialogOpen: boolean;
    youtubeVideoStatus: YoutubeVideoStatus | undefined;

    imageSelectionPopover: boolean;
    imageSelectionAnchor: HTMLElement | undefined;
}

export class DialogManager extends AppStateManager<DialogManagerState> {
    @observable.ref
    public dialog: InputDialogOptions | null = null;

    @observable.ref
    public loadingDialog: LoadingDialogConfiguration | null = null;

    @observable.ref
    public colorDialog: ColorDialogConfiguration | null = null;

    @observable.ref
    public errorDialog: ErrorDialogConfiguration | null = null;

    @observable.ref
    public qrDialog: QrDialogConfiguration | null = null;

    @computed
    public get dialogVisible(): boolean {
        return (
            this.dialog != null ||
            this.loadingDialog != null ||
            this.colorDialog != null ||
            this.errorDialog != null ||
            this.qrDialog != null ||
            this.state.cookieDialog
        );
    }

    constructor() {
        super();
        makeObservable(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.showInputDialog = this.showInputDialog.bind(this);
        this.showColorDialog = this.showColorDialog.bind(this);
        this.showModalDialog = this.showModalDialog.bind(this);
        this.triggerErrorDialog = this.triggerErrorDialog.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onConnectionError = this.onConnectionError.bind(this);
        this.onCoreShowMessage = this.onCoreShowMessage.bind(this);
        this.updateDialog = this.updateDialog.bind(this);
        this.copyTextToClipboard = this.copyTextToClipboard.bind(this);
    }

    public override initialize(app: App): void {
        super.initialize(app);

        window.addEventListener('resize', this.onResize);

        this.app.coreApp.appCallbacks.showMedia = this.showOAPMedia;
        this.app.coreApp.appCallbacks.showMessage = this.onCoreShowMessage;

        if (appInfo != null) {
            reaction(
                () => this.state.youtubeDialogOpen,
                () => {
                    if (this.state.youtubeDialogOpen) {
                        this.app.mobileEvents.statusbar.trigger('saveState');
                        this.app.mobileEvents.statusbar.trigger('statusbarFontDark');
                    } else {
                        this.app.mobileEvents.statusbar.trigger('restoreState');
                    }
                }
            );

            PlayVideoProgress.addListener((event: PlayVideoProgressEvent): void => {
                this.updateState({
                    youtubeVideoStatus: event.state
                });
            });
        }
    }

    public showDialog(dialog: InputDialogOptions, sentryBreadcrumb: string = 'no title set'): void {
        addBreadcrumb({
            category: 'LatestDialog',
            message: sentryBreadcrumb
        });

        this.updateDialog(
            DialogType.DEFAULT,
            dialog
        );
    }

    public triggerErrorDialog(errorDialog: ErrorDialogConfiguration | null): void {
        this.updateDialog(DialogType.ERROR, errorDialog);
    }

    public closeDialog(whichDialog: DialogType = DialogType.DEFAULT): void {
        this.app.backButtonManager.removeLastAction();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.updateDialog(whichDialog as any, null);
    }

    public showLoadingDialog = (label: string, closeCallback?: () => void, value?: number | string): void => {
        if (value && typeof value === 'number' && isNaN(value)) {
            value = undefined;
        }
        this.updateDialog(
            DialogType.LOADING,
            {label: label, closeCallback, value: value}
        );
    };

    @bind
    public setImageSelectionAnchor(imageSelectionAnchor: HTMLElement | undefined): void {
        this.updateState({imageSelectionAnchor});
    }

    @bind
    public async showCancelDialog<T>(promise: Promise<T>, label: string): Promise<T | null> {
        let canceled: boolean = false;
        const cancelCallback: () => void = () => {
            this.closeDialog(DialogType.LOADING);
            canceled = true;
        };

        this.showLoadingDialog(label, cancelCallback);

        const result: T = await promise;

        if (canceled) {
            return null;
        }

        this.closeDialog(DialogType.LOADING);

        return result;
    }

    public showDialogWithUrl(urlForDialog: string, title?: string, withLink: boolean = true): void {
        const config: InputDialogConfig = withLink ? {
            title: isNullOrEmpty(title) ? i18next.t('Link') : title,
            subTitle: [<Link href={urlForDialog} target="_blank" key="link">{i18next.t('Link')}</Link>],
            buttons: [
                {
                    label: i18next.t('Ok'),
                    callback: this.closeDialog,
                    key: 'submit',
                }
            ]
        } : {
            title: isNullOrEmpty(title) ? i18next.t('Link') : title,
            buttons: [
                {
                    label: i18next.t('Abort'),
                    callback: this.closeDialog,
                    key: 'cancel'
                },
                {
                    label: i18next.t('Continue'),
                    callback: () => { window.open(urlForDialog, '_blank'); },
                    key: 'continue'
                },
            ]
        };

        this.showDialog(
            {
                InputDialogConfig: config,
                onClose: this.closeDialog,
                onForceClose: this.closeDialog,
                onBackdropClick: this.closeDialog
            },
            'simple dialog with url'
        );
}
    public async showModalDialog(
        config: ModalInputDialogConfig,
        sentryBreadcrumb: string = 'no title set',
        okLabel: string = i18next.t('Ok'),
        cancelLabel: string = i18next.t('Cancel'),
        onBackgroundClick?: (resolve: (value: boolean) => void, reject: (error: Error) => void) => void,
    ): Promise<boolean> {
        return new Promise<boolean>((resolve: (value: boolean) => void, reject: (error?: Error) => void) => {
            this.showDialog(
                {
                    InputDialogConfig: {
                        ...config,
                        buttons: [
                            {label: cancelLabel, key: 'cancel-export', callback: (): void => { resolve(false); }},
                            {label: okLabel, key: 'submit', callback: (): void => { resolve(true); }},
                        ]
                    },
                    onClose: (): void => { this.closeDialog(); resolve(false); },
                    onForceClose: (): void => { this.closeDialog(); resolve(false); },
                    onBackdropClick: (): void => {
                        if (onBackgroundClick != null) {
                            onBackgroundClick(resolve, reject);
                        } else {
                            this.closeDialog();
                            resolve(false);
                        }
                    }
                },
                `${sentryBreadcrumb} (modal)`
            );
        });
    }

    public async showInputDialog<ResultType = string | number>(
        inputDialogConfig: Partial<InputDialogConfig>,
        onClose?: (resolve: (value?: ResultType) => void, reject: (error: Error) => void) => void,
        okText: string = i18next.t('Ok'),
        cancelText: string = i18next.t('Cancel'),
        onCancel: (
            resolve: (value?: ResultType) => void,
            reject: (error: Error) => void
        ) => void = (resolve: (value?: ResultType) => void) => {
            resolve();
        }
    ): Promise<ResultType | undefined> {
        return new Promise<ResultType | undefined>((resolve: (value?: ResultType) => void, reject: (error: Error) => void): void => {

            this.app.backButtonManager.add(async () => {
                await this.app.backButtonManager.callLastAction();
                this.closeDialog();
                onCancel(resolve, reject);
            });

            const cancel: VoidFunction = () => {
                this.app.backButtonManager.removeLastAction();
                this.closeDialog();
                onCancel(resolve, reject);
            };

            const close: VoidFunction = (): void => {
                this.app.backButtonManager.removeLastAction();
                if (onClose != null) {
                    onClose(resolve, reject);
                } else {
                    cancel();
                }
            };

            this.updateDialog(
                DialogType.DEFAULT,
                {
                    InputDialogConfig: {
                        autoFocus: true, // defaultSetting
                        ...inputDialogConfig,
                        buttons: [
                            {label: cancelText, key: 'input-cancel', callback: cancel},
                            {label: okText, key: 'input-ok', checkInput: true, callback: (value?: ResultType) => {
                                this.app.backButtonManager.removeLastAction();
                                this.closeDialog();
                                resolve(value);
                            } },
                        ]
                    },
                    input: true,
                    onClose: close,
                    onForceClose: close,
                    onBackdropClick: close
                }
            );
        });
    }

    public async showColorDialog(
        color: string | undefined,
        colors: Array<string | undefined> = ColorDialogColors,
        extended: boolean = false
    ): Promise<string | undefined> {
        return new Promise<string | undefined>((resolve) => {
            this.updateDialog(
                DialogType.COLOR,
                {
                    color,
                    colors,
                    resolve: (newColor: string | undefined) => { this.closeDialog(DialogType.COLOR); resolve(newColor); },
                    extended
                }
            );
        });
    }

    @bind
    public showDataErrorDialog(message: string, error: Error, articleInfo: {manufacturer?: string, articleNumber?: string}, useMessageAsSubject?: boolean): void {
        // Note: the function is called again if the user toggles the detail information, therefore we have to keep
        // information about the current state
        const state: {firstCall: boolean, showDetails: boolean} = getCachedValue(error, {firstCall: true, showDetails: false});

        const manufacturerText: string | undefined = articleInfo.manufacturer == null ?
            undefined :
            // escapeValue=false because manufacturer may include HTML like &amp;
            i18next.t('Manufacturer: {{manufacturer}}', {...articleInfo, interpolation: { escapeValue: false }});
        const articleCodeText: string | undefined = articleInfo.articleNumber == null ? undefined : i18next.t('Articlecode: {{articleNumber}}', articleInfo);

        if (state.firstCall) {
            state.firstCall = false;
            void captureDataException(error, this.app.eaiwsSession);
        }

        this.app.dialogManager.showDialog(
            {
                InputDialogConfig: {
                    title: <div onClick={debugTap}>{i18next.t('Data error')}</div>,
                    subTitle: isNotNullOrEmpty(message) ?
                        [manufacturerText, articleCodeText, message] :
                        [manufacturerText, articleCodeText],
                    buttons: [
                        getDataErrorFeedbackButton(FEEDBACK_EMAIL, error, articleInfo.manufacturer, articleInfo.articleNumber, message, useMessageAsSubject),
                        {
                            label: i18next.t('Details'),
                            key: 'details',
                            preventCloseCall: true,
                            callback: () => {
                                state.showDetails = !state.showDetails;
                                // Note: its not the best solution but right now
                                // we have to call this function again in order to
                                // update the DOM
                                this.showDataErrorDialog(message, error, articleInfo, useMessageAsSubject);
                            }
                        },
                        {label: i18next.t('Close'), key: 'close'},
                    ].filter(notNull),
                    elementBelowText: state.showDetails ? <DialogErrorMessage error={error}/> : null
                },
                onForceClose: doNothing
            },
            'DataError'
        );
    }

    public showIframe = (iframeUri: string, iframeTitle: string = '', iframeBarStyle: IframeBarStyle = 'default'): void => {
        this.updateState({iframeUri, iframeTitle, iframeBarStyle});
    };

    public hideIframe = (): void => {
        this.updateState({iframeUri: undefined});
    };

    @bind
    public async showCookieDialog(): Promise<CookieUserSetting> {
        return new Promise<CookieUserSetting>((resolveCookieDialog) => {
            this.updateState({
                cookieDialog: true,
                resolveCookieDialog,
            });
        });
    }

    @bind
    public closeCookieDialog(): void {
        this.updateState({cookieDialog: false, resolveCookieDialog: null});
    }

    /**
     * Workaround for iOS 12.x issue with download and popup blocker in Safari
     * @param href
     * @param download
     * @param target
     */
    public downloadHelper(
        href: string,
        download: string,
        target: '_blank' | '_self' | '_parent' | '_top' = '_blank',
        anyIOS: boolean = false,
    ): void {
        if (ios && (anyIOS || getIOSVersion() < 13)) {
            return this.showDialog(
                {
                    InputDialogConfig: {
                        title: i18next.t('Do you want to download "{{filename}}"?', { filename: iosFilenameHelper(href, download)}),
                        buttons: [
                            {
                                label: i18next.t('Download'),
                                key: 'download',
                                type: 'link',
                                href,
                                download,
                            },
                            {
                                label: i18next.t('Cancel'), key: 'close'
                            },
                        ],
                    },
                    onForceClose: doNothing
                },
                'DownloadHelper'
            );
        }
        return downloadUrl(href, download, target);
    }

    @bind
    public async openYoutube(youtubeLinkId: string, name?: string): Promise<void> {
        if (inPlanner) {
            const a: HTMLAnchorElement = document.createElement('a');
            a.href = `https://www.youtube.com/watch?v=${youtubeLinkId}`;
            a.rel = 'noopener noreferrer';
            a.target = '_blank';
            return a.click();
        }

        this.updateState({
            youtubeLinkId: youtubeLinkId,
            youtubeTitle: name ? name : '',
            youtubeDialogOpen: !(ios || android)
        });

        if ((ios || android)) {
            await startVideoPlayback({ video_id: youtubeLinkId, context: 'pim_video' });
        }
    }

    @bind
    public closeYoutube(): void {
        this.updateState({
            youtubeLinkId: undefined,
            youtubeTitle: undefined,
            youtubeDialogOpen: false,
            youtubeVideoStatus: 'ended',
        });
    }

    @bind
    public async showRemoveArticleDialog(): Promise<boolean> {
        return this.showModalDialog(
            {
                title: i18next.t('Delete article'),
                subTitle: [i18next.t('Are you sure you want to delete the article?')],
            },
            'RemoveArticleConfirmation',
            i18next.t('Yes')
        );
    }

    @bind
    public async showRemoveAllArticlesDialog(): Promise<boolean> {
        return this.showModalDialog(
            {
                title: i18next.t('Delete all articles'),
                subTitle: [i18next.t('Are you sure you want to delete all articles?')],
            },
            'RemoveArticleConfirmation',
            i18next.t('Yes')
        );
    }

    public onConnectionError(retryCallback: VoidFunction): void {
        this.showDialog(
            {
                InputDialogConfig: {
                    title: i18next.t('Connection interrupted'),
                    buttons: [
                        {label: i18next.t('Connect'), key: 'reconnect', callback: (): void => {
                            retryCallback();
                            this.closeDialog();
                        }}
                    ],
                },
                onClose: noClose,
                onForceClose: (): void => {
                    // FixMe: ...
                    throw new Error('Not supported');
                },
            },
            'NetworkError'
        );
    }

    public async sendUrlToPconBasket(file_open_url: string, onlySelectionUrl?: string): Promise<void> {
        if (isNullOrEmpty(file_open_url)) {
            return;
        }

        if (isNullOrEmpty(onlySelectionUrl)) {
            return this.showDialogWithUrl(
                file_open_url,
                i18next.t('Transfer to pCon.basket'),
                false
            );
        }

        let selection: boolean = false;

        return this.app.dialogManager.showDialog({
                InputDialogConfig: {
                    title: i18next.t('Transfer to pCon.basket'),
                    subTitle: [
                        <FormControl fullWidth={true} key="selection">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => { selection = checked; }}
                                        name="selection"
                                    />
                                }
                                name="selection"
                                label={i18next.t('Only selection')}
                            />
                        </FormControl>
                    ],
                    buttons: [
                        {
                            label: i18next.t('Abort'),
                            callback: this.app.dialogManager.closeDialog,
                            key: 'cancel'
                        },
                        {
                            label: i18next.t('Continue'),
                            callback: () => {
                                if (selection) {
                                    trackEvent({
                                        category: 'Article list',
                                        action: 'transfer',
                                        name: 'basket'
                                    });
                                    window.open(onlySelectionUrl, '_blank');
                                } else {
                                    window.open(file_open_url, '_blank');
                                }
                            },
                            key: 'continue'
                        },
                    ]
                },
                onClose: this.app.dialogManager.closeDialog,
                onForceClose: this.app.dialogManager.closeDialog,
                onBackdropClick: this.app.dialogManager.closeDialog
            },
            'ToBasketButton'
        );
    }

    public updateDialog(type: DialogType.DEFAULT, config: InputDialogOptions | null);
    public updateDialog(type: DialogType.LOADING, config: LoadingDialogConfiguration | null);
    public updateDialog(type: DialogType.COLOR, config: ColorDialogConfiguration | null);
    public updateDialog(type: DialogType.ERROR, config: ErrorDialogConfiguration | null);
    public updateDialog(type: DialogType.QR, config: QrDialogConfiguration | null);
    public updateDialog(type: DialogType.ALL, config: null);
    @action
    public updateDialog(type: DialogType, config: null): void {
        switch (type) {
            case DialogType.COLOR:
                this.colorDialog = config;
                this.dialog = null;
                this.errorDialog = null;
                this.loadingDialog = null;
                this.qrDialog = null;
                break;
            case DialogType.DEFAULT:
                this.colorDialog = null;
                this.dialog = config;
                this.errorDialog = null;
                this.loadingDialog = null;
                this.qrDialog = null;
                break;
            case DialogType.ERROR:
                this.colorDialog = null;
                this.dialog = null;
                this.errorDialog = config;
                this.loadingDialog = null;
                this.qrDialog = null;
                break;
            case DialogType.LOADING:
                this.colorDialog = null;
                this.dialog = null;
                this.errorDialog = null;
                this.loadingDialog = config;
                this.qrDialog = null;
                break;
            case DialogType.QR:
                this.colorDialog = null;
                this.dialog = null;
                this.errorDialog = null;
                this.loadingDialog = null;
                this.qrDialog = config;
                break;
            default:
                this.colorDialog = null;
                this.dialog = null;
                this.errorDialog = null;
                this.loadingDialog = null;
                this.qrDialog = null;
        }
    }

    public async copyTextToClipboard(text: string): Promise<void> {
        if (appInfo != null) {
            await shareText(text);
        } else if (!(await copyToClipBoard(text))) {
            return this.app.messageManager.showMessage(i18next.t('Copy failed'));
        }

        if (text.startsWith('http')) {
            this.app.messageManager.showMessage(i18next.t('Link copied to clipboard'));
        } else {
            this.app.messageManager.showMessage(i18next.t('Text copied to clipboard'));
        }
    }

    protected onResize(): void {
        this.updateState({showDialogFullscreen: this.fullscreenCondition()});
    }

    protected fullscreenCondition(): boolean {
        return window.innerWidth <= 568 || window.innerHeight <= 568;
    }

    @bind
    protected showOAPMedia(media: AppCallbacks.Media): void {
        void this.openYoutube(media.id);
    }

    protected onCoreShowMessage(message: AppCallbacks.Message, options: AppCallbacks.ShowMessageOptions): void {
        this.app.messageManager.showMessage(
            <React.Fragment>
                {multilineTextToArray(message.text, true).map(textLine)}
            </React.Fragment>
        );
    }

    protected getInitialState(): DialogManagerState {
        return {
            iframeUri: undefined,
            iframeTitle: '',
            iframeBarStyle: 'default',

            showDialogFullscreen: this.fullscreenCondition(),

            cookieDialog: false,
            resolveCookieDialog: null,

            youtubeLinkId: undefined,
            youtubeTitle: undefined,
            youtubeDialogOpen: false,
            youtubeVideoStatus: undefined,

            imageSelectionPopover: false,
            imageSelectionAnchor: undefined,
        };
    }
}

/**
 * iOS does not use HTML5 download attribute for filename so we must strip the the filename from url
 * @param url
 * @param fallback
 */
function iosFilenameHelper(url: string, fallback: string): string {
    const result: string = url.substring(url.lastIndexOf('/') + 1);
    if (isNotNullOrEmpty(result)) {
        return result;
    }
    return fallback;
}